import { Suspense, lazy, useCallback, useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import authHook from '@hooks/useAuth'
import navigationHook from '@hooks/useNavigation'

import { withStreamChatManagedConnector } from '@HOCs/withStreamChatManagedConnector'

import { StreamChatAnonymousConnection } from '@components/StreamChatAnonymousConnection'
import { StreamChatPrivateConnection } from '@components/StreamChatPrivateConnection'

const BuyerOrVisitorPublicSolution_v2 = lazy(() =>
  import('@pages/PublicSolution_v2').then((module) => {
    return {
      default: module.PublicSolution_v2,
    }
  })
)

const LandingPageTemplateLazy = lazy(() =>
  import('@pages/LandingPage/Template').then((module) => {
    return {
      default: module.LandingPageTemplate,
    }
  })
)

const BuyerPublicSolution_V2_ViewWithChatConnection =
  withStreamChatManagedConnector(
    BuyerOrVisitorPublicSolution_v2,
    StreamChatPrivateConnection,
    { authenticated: true }
  )

const VisitorSolution_V2_ViewWithChatConnection =
  withStreamChatManagedConnector(
    BuyerOrVisitorPublicSolution_v2,
    StreamChatAnonymousConnection,
    { authenticated: false }
  )

const SolutionViewMiddleware = () => {
  const { isFetchingUserData, isBuyer } = authHook.useAuth()
  const location = useLocation()
  const { solutionIdOrSlug } = useParams()
  const { navigateTo, changeUrlPath } = navigationHook.useNavigation()

  const removeBuyerFromSolutionPath = useCallback(() => {
    if (location.pathname.startsWith('/buyer/solutions/')) {
      const newPath = `/solutions/${solutionIdOrSlug}`
      changeUrlPath({ newPath })
    }
  }, [changeUrlPath, location.pathname, solutionIdOrSlug])

  useEffect(() => {
    if (isBuyer() && !location.pathname.startsWith('/buyer/solutions/')) {
      const newPath = `/buyer${location.pathname}`

      navigateTo({
        path: newPath,
        replace: true,
      })

      return
    }

    removeBuyerFromSolutionPath()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isBuyer, navigateTo, removeBuyerFromSolutionPath])

  const ComponentToRender = useMemo(() => {
    if (isBuyer()) {
      return <BuyerPublicSolution_V2_ViewWithChatConnection />
    }

    return (
      <LandingPageTemplateLazy>
        <VisitorSolution_V2_ViewWithChatConnection />
      </LandingPageTemplateLazy>
    )
  }, [isBuyer])

  if (isFetchingUserData) {
    return <></>
  }

  return <Suspense fallback={<></>}>{ComponentToRender}</Suspense>
}

export { SolutionViewMiddleware }
